import { Datepicker } from '@/components/Datepicker';
import { dateToUrlString } from '@/utils/dateUtils';
import { MAX_CAMPAIGN_PERIOD_DAYS, Tooltip } from '@analytical-alley/ui';
import { Label } from 'flowbite-react';
import moment from 'moment';
import React, { ReactElement, useMemo } from 'react';

interface DateRangeProps {
    minDate?: Date;
    maxDate?: Date;
    searchStartDate: string | undefined;
    searchEndDate: string | undefined;
    onSetSearch: (search: {
        startDate?: string | undefined;
        endDate?: string | undefined;
    }) => void;
    tooltipContent?: ReactElement;
    disabled?: boolean;
}

export const DateRange = ({
    maxDate,
    minDate,
    searchStartDate,
    searchEndDate,
    onSetSearch,
    tooltipContent,
    disabled,
}: DateRangeProps) => {
    const startDate = useMemo(
        () =>
            searchStartDate
                ? moment.utc(searchStartDate, 'DD-MM-YYYY').toDate()
                : null,
        [searchStartDate],
    );
    const endDate = useMemo(
        () =>
            searchEndDate
                ? moment.utc(searchEndDate, 'DD-MM-YYYY').toDate()
                : null,
        [searchEndDate],
    );

    return (
        <Tooltip placement="bottom">
            <Tooltip.Trigger asChild className="flex gap-4 items-start">
                <div>
                    <div className="mb-1 block text-left">
                        <Label
                            className="font-mono font-normal"
                            htmlFor="startDate"
                        >
                            Start date
                        </Label>
                    </div>
                    <Datepicker
                        disabled={disabled}
                        dateRangeType="min"
                        minDate={minDate}
                        maxDate={endDate || maxDate}
                        placeholder={'Select start date'}
                        onSelectedDateChanged={(val) => {
                            onSetSearch({
                                startDate: val
                                    ? dateToUrlString(val)
                                    : undefined,
                            });
                        }}
                        value={startDate}
                        id="startDate"
                        shadow
                        compact
                        showClearButton={false}
                        showTodayButton={false}
                    />
                </div>
                <div>
                    <div className="mb-1 block text-left">
                        <Label
                            className="font-mono font-normal"
                            htmlFor="endDate"
                        >
                            End date
                        </Label>
                    </div>
                    <Datepicker
                        disabled={disabled}
                        dateRangeType="max"
                        minDate={startDate || minDate}
                        maxDate={maxDate}
                        placeholder={'Select end date'}
                        onSelectedDateChanged={(val) => {
                            onSetSearch({
                                endDate: val ? dateToUrlString(val) : undefined,
                            });
                        }}
                        value={endDate}
                        id="endDate"
                        shadow
                        compact
                        showClearButton={false}
                        showTodayButton={false}
                    />
                </div>
            </Tooltip.Trigger>
            {tooltipContent && (
                <Tooltip.Content className="tooltip-content">
                    {tooltipContent}
                </Tooltip.Content>
            )}
        </Tooltip>
    );
};
